import React, { useRef, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Download, Calendar, MapPin, User, Ticket, Lightbulb } from "lucide-react";
import { Button } from "./ui/button";

// Cultural facts for tickets
const culturalFacts = [
  "DJ Kool Herc a inventé le 'breakbeat' en 1973 dans le Bronx, donnant naissance au Hip Hop.",
  "Fela Kuti a créé l'Afrobeat en mélangeant jazz, funk et rythmes yoruba dans les années 70.",
  "Le terme 'Rhythm and Blues' a été inventé en 1949 par Jerry Wexler.",
  "Le dancehall est né dans les sound systems de Kingston dans les années 70.",
  "Les battles de rap ont remplacé les conflits de gangs dans le Bronx des années 70.",
  "Bob Marley a popularisé le reggae à l'international depuis la Jamaïque.",
  "Le sampling, technique clé du Hip Hop, transforme des extraits musicaux en nouvelles créations."
];

export const TicketQRCode = ({ order, item, index }) => {
  const ticketRef = useRef(null);
  
  // Get a random fact for each ticket render
  const culturalFact = useMemo(() => {
    return culturalFacts[Math.floor(Math.random() * culturalFacts.length)];
  }, []); // Empty deps = new random fact each time component mounts

  // Generate unique ticket ID combining order ID and item index
  const ticketId = `${order.id}-${index}`;
  
  // QR code data - just the order ID for simplicity
  const qrData = JSON.stringify({
    orderId: order.id,
    ticketIndex: index
  });

  const handleDownload = () => {
    const ticketElement = ticketRef.current;
    if (!ticketElement) return;

    // Create a canvas from the ticket element
    import('html2canvas').then(({ default: html2canvas }) => {
      html2canvas(ticketElement, {
        backgroundColor: '#1a1714',
        scale: 2
      }).then(canvas => {
        const link = document.createElement('a');
        link.download = `billet-${order.id}-${index + 1}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      });
    }).catch(() => {
      // Fallback: just print the page
      window.print();
    });
  };

  return (
    <div className="space-y-4">
      {/* Ticket Card */}
      <div 
        ref={ticketRef}
        className="bg-gradient-to-br from-[#2a2520] to-[#1a1714] rounded-xl overflow-hidden border border-white/10 shadow-2xl"
      >
        {/* Header with event info */}
        <div className="bg-primary/20 px-6 py-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-primary font-semibold uppercase tracking-wider">TIKA Billet</p>
              <h3 className="text-lg font-bold text-white mt-1">{item.event_title}</h3>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">#{index + 1}</p>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="p-6 flex flex-col sm:flex-row gap-6">
          {/* QR Code */}
          <div className="flex-shrink-0 flex flex-col items-center">
            <div className="bg-white p-3 rounded-lg">
              <QRCodeSVG 
                value={qrData}
                size={120}
                level="H"
                includeMargin={false}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Scanner à l'entrée
            </p>
          </div>

          {/* Event Details */}
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-primary" />
              <span className="text-muted-foreground">Artiste:</span>
              <span className="text-white font-medium">{item.event_artist}</span>
            </div>
            
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-primary" />
              <span className="text-muted-foreground">Date:</span>
              <span className="text-white font-medium">{item.event_date}</span>
            </div>
            
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-muted-foreground">Lieu:</span>
              <span className="text-white font-medium">{item.event_venue}</span>
            </div>
            
            <div className="flex items-center gap-2 text-sm">
              <Ticket className="w-4 h-4 text-primary" />
              <span className="text-muted-foreground">Type:</span>
              <span className="text-white font-medium">{item.ticket_type}</span>
            </div>

            {/* Order ID */}
            <div className="pt-2 mt-2 border-t border-white/10">
              <p className="text-xs text-muted-foreground">
                Commande: <span className="text-white font-mono">{order.id.slice(0, 8)}...</span>
              </p>
            </div>
          </div>
        </div>

        {/* Footer with cultural fact */}
        <div className="bg-primary/10 px-6 py-4 border-t border-white/10">
          <div className="flex items-start gap-2">
            <Lightbulb className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-primary font-semibold mb-1">Le savais-tu ?</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {culturalFact}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <Button
        onClick={handleDownload}
        variant="outline"
        size="sm"
        className="w-full"
        data-testid={`download-ticket-${index}`}
      >
        <Download className="w-4 h-4 mr-2" />
        Télécharger ce billet
      </Button>
    </div>
  );
};
